import { useMemo, useState } from 'react';
import { UIPaginatedSelectOption } from '@/ui';
import { useFetchUsersPaginated } from '@/companies/queries';
import { useDebouncedValue } from '@/common/hooks';

export type FetchTeamMemberOptionsResult = {
    options: UIPaginatedSelectOption[];
    search: string;
    setSearch: (query: string) => void;
    status: 'error' | 'success' | 'pending';
    isFetchingNextPage: boolean;
    hasNextPage: boolean;
    fetchNextPage: () => void;
    refetch: () => void;
};

/**
 * The members of a team, as searchable pages for a device owner picker. The
 * server does the team scoping, so the caller never filters the list itself.
 */
export function useFetchTeamMemberOptions(params: {
    companyId: string;
    teamId: string | undefined;
}): FetchTeamMemberOptionsResult {
    const { companyId, teamId } = params;

    const [search, setSearch] = useState('');
    const debouncedSearch = useDebouncedValue(search.trim());

    const {
        users,
        status,
        isFetchingNextPage,
        hasNextPage,
        fetchNextPage,
        refetch
    } = useFetchUsersPaginated({
        companyId,
        search: debouncedSearch,
        teamIds: teamId ? [teamId] : [],
        membership: 'all',
        sortBy: 'name',
        sortOrder: 'asc',
        // Without a team there is nothing to scope to, and an unscoped query
        // would pull the whole company.
        enabled: !!teamId
    });

    const options = useMemo<UIPaginatedSelectOption[]>(
        () =>
            users.map((user) => ({
                value: user.id,
                // Invited users have no name yet, so the email is the label.
                label: user.joined
                    ? `${user.firstName ?? ''} ${user.lastName ?? ''}`.trim() ||
                      user.email
                    : user.email,
                subLabel: user.joined ? user.email : undefined,
                image: user.image
            })),
        [users]
    );

    return {
        options,
        search,
        setSearch,
        status,
        isFetchingNextPage,
        hasNextPage,
        fetchNextPage,
        refetch
    };
}
